// Task #4624 — Admin tool to manually merge two duplicate member profiles.
// Search/select a canonical row and a duplicate row, confirm, then call
// POST /api/admin/members/merge (which wraps storage.mergeMembers).
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, GitMerge, X, ArrowRight } from "lucide-react";

interface MergeSearchRow {
  id: number;
  name: string;
  status: string;
  padsplitMemberId: string | null;
  occupantId: string | null;
  createdAt: string;
  hostUserId: number | null;
  hostEmail: string | null;
  // Every members_users link (a duplicate linked to 2+ hosts is exactly what
  // trips the cross-host merge gate — the operator needs to see all of them).
  hosts?: Array<{ id: number; email: string }>;
  chatCount: number;
}

function hostsOf(row: MergeSearchRow): Array<{ id: number; email: string }> {
  if (row.hosts && row.hosts.length > 0) return row.hosts;
  return row.hostUserId != null ? [{ id: row.hostUserId, email: row.hostEmail ?? String(row.hostUserId) }] : [];
}

function hostLabel(row: MergeSearchRow): string {
  const hosts = hostsOf(row);
  if (hosts.length === 0) return "— (no host link)";
  return hosts.map((h) => h.email).join(", ") + (hosts.length > 1 ? ` (${hosts.length} hosts)` : "");
}

type Slot = "canonical" | "duplicate";

function MemberSummary({ row, onClear, testIdPrefix }: { row: MergeSearchRow; onClear: () => void; testIdPrefix: string }) {
  return (
    <div className="border rounded-md p-3 bg-muted/30 flex items-start justify-between gap-2" data-testid={`${testIdPrefix}-selected-${row.id}`}>
      <div className="text-sm space-y-0.5">
        <div className="font-medium">{row.name} <span className="text-xs text-muted-foreground">#{row.id}</span></div>
        <div className="text-xs text-muted-foreground">Host: {hostLabel(row)}</div>
        <div className="text-xs text-muted-foreground">
          PadSplit ID: {row.padsplitMemberId ?? "—"} · Occupant ID: {row.occupantId ?? "—"} · {row.chatCount} chat{row.chatCount === 1 ? "" : "s"}
        </div>
      </div>
      <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0" onClick={onClear} data-testid={`${testIdPrefix}-clear`}>
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function MemberSearchBox({ slot, label, hint, selected, onSelect, onClear, excludeId }: {
  slot: Slot;
  label: string;
  hint: string;
  selected: MergeSearchRow | null;
  onSelect: (row: MergeSearchRow) => void;
  onClear: () => void;
  excludeId: number | null;
}) {
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<MergeSearchRow[] | null>(null);

  const searchMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (query: string) => {
      const res = await apiRequest("GET", `/api/admin/members/merge-search?q=${encodeURIComponent(query)}`);
      return (await res.json()) as MergeSearchRow[];
    },
    onSuccess: (rows) => setResults(rows),
    onError: (err: any) => toast({ title: "Search failed", description: parseErrorMessage(err, "Couldn't search members. Please try again."), variant: "destructive" }),
  });

  const runSearch = () => {
    const query = q.trim();
    if (query.length < 2) {
      toast({ title: "Search too short", description: "Enter at least 2 characters.", variant: "destructive" });
      return;
    }
    searchMutation.mutate(query);
  };

  return (
    <div className="flex-1 min-w-0 space-y-2">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      {selected ? (
        <MemberSummary row={selected} onClear={onClear} testIdPrefix={`merge-${slot}`} />
      ) : (
        <>
          <div className="flex gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
              placeholder="Name, member ID, PadSplit ID, or occupant ID"
              className="h-8 text-sm"
              data-testid={`input-merge-search-${slot}`}
            />
            <Button size="sm" variant="outline" onClick={runSearch} disabled={searchMutation.isPending} data-testid={`button-merge-search-${slot}`}>
              {searchMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          {results !== null && (
            <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
              {results.filter((r) => r.id !== excludeId).length === 0 ? (
                <div className="text-xs text-muted-foreground p-3 text-center" data-testid={`text-merge-no-results-${slot}`}>No matching members.</div>
              ) : (
                results.filter((r) => r.id !== excludeId).map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="w-full text-left p-2 hover:bg-muted/50 text-sm"
                    onClick={() => { onSelect(r); setResults(null); setQ(""); }}
                    data-testid={`row-merge-result-${slot}-${r.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{r.name} <span className="text-xs text-muted-foreground">#{r.id}</span></span>
                      <Badge variant="outline" className="text-[10px] shrink-0">{r.chatCount} chats</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {hostsOf(r).length > 0 ? hostLabel(r) : "no host link"} · PadSplit: {r.padsplitMemberId ?? "—"} · Occupant: {r.occupantId ?? "—"}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function MemberMergePanel() {
  const { toast } = useToast();
  const [canonical, setCanonical] = useState<MergeSearchRow | null>(null);
  const [duplicate, setDuplicate] = useState<MergeSearchRow | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mergeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/members/merge", {
        canonicalId: canonical!.id,
        duplicateId: duplicate!.id,
      });
      return await res.json();
    },
    onSuccess: () => {
      toast({
        title: "Members merged",
        description: `${duplicate?.name} (#${duplicate?.id}) was merged into ${canonical?.name} (#${canonical?.id}).`,
      });
      setConfirmOpen(false);
      setCanonical(null);
      setDuplicate(null);
    },
    onError: (err: any) => {
      setConfirmOpen(false);
      toast({ title: "Merge failed", description: parseErrorMessage(err, "Couldn't merge these members. Please try again."), variant: "destructive" });
    },
  });

  // Mirror the server gate exactly: the UNION of both members' distinct host
  // links must be ≤ 1 (a single member linked to 2 hosts is just as blocked
  // as two members on different hosts; unlinked orphans merge freely).
  const crossHost = (() => {
    if (!canonical || !duplicate) return false;
    const union = new Set([...hostsOf(canonical), ...hostsOf(duplicate)].map((h) => h.id));
    return union.size > 1;
  })();

  return (
    <Card className="p-4">
      <div className="mb-4">
        <h2 className="font-semibold text-base">Merge Duplicate Members</h2>
        <p className="text-xs text-muted-foreground max-w-2xl">
          Manually merge two duplicate member profiles the automatic dedup couldn't safely match.
          All chats, tasks, reviews, and links move to the canonical row; the duplicate is soft-deleted
          (stamped merged-into) and disappears from the host's inbox. Pick the row with the most chat
          history as the canonical. Cross-host merges are blocked. This cannot be undone from the UI.
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-4 items-stretch">
        <MemberSearchBox
          slot="canonical"
          label="Canonical member (kept)"
          hint="The row that survives — usually the one with the most chat history."
          selected={canonical}
          onSelect={setCanonical}
          onClear={() => setCanonical(null)}
          excludeId={duplicate?.id ?? null}
        />
        <div className="hidden md:flex items-center pt-8">
          <ArrowRight className="h-5 w-5 text-muted-foreground rotate-180" />
        </div>
        <MemberSearchBox
          slot="duplicate"
          label="Duplicate member (merged away)"
          hint="The row that gets soft-deleted and folded into the canonical."
          selected={duplicate}
          onSelect={setDuplicate}
          onClear={() => setDuplicate(null)}
          excludeId={canonical?.id ?? null}
        />
      </div>
      {crossHost && (
        <p className="text-xs text-destructive mt-3" data-testid="text-merge-cross-host-warning">
          These members' host links span more than one host — cross-host merges are not allowed.
          Heal the stale link first if one of them shouldn't belong to that host.
        </p>
      )}
      <div className="mt-4">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!canonical || !duplicate || crossHost || mergeMutation.isPending}
          data-testid="button-open-merge-confirm"
        >
          <GitMerge className="h-4 w-4 mr-1.5" /> Merge members…
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(open) => { if (!mergeMutation.isPending) setConfirmOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge these members?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium text-foreground">{duplicate?.name} (#{duplicate?.id})</span> will be merged into{" "}
                  <span className="font-medium text-foreground">{canonical?.name} (#{canonical?.id})</span>.
                </p>
                <p>
                  All of the duplicate's chats, tasks, reviews, and links move to the canonical row. The duplicate
                  is soft-deleted and disappears from the host's inbox. This cannot be undone from the UI.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mergeMutation.isPending} data-testid="button-cancel-merge">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); mergeMutation.mutate(); }}
              disabled={mergeMutation.isPending}
              data-testid="button-confirm-merge"
            >
              {mergeMutation.isPending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <GitMerge className="h-4 w-4 mr-1.5" />}
              Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
